import { parse } from '@babel/parser';
import traverse from '@babel/traverse';
import * as t from '@babel/types';
import { DATA_FILE_PATTERN, quoteString } from './dataRecordPatch';

/**
 * The list behind the data grid (hot edit plan, phase 3): the array in `src/data/*.ts` that holds
 * the selected row's record, read as records and fields, and edited with small text splices so the
 * rest of the file keeps its formatting. Only plain literal fields (string, number, boolean) are
 * editable; renaming fields or changing their type is out of scope.
 */
export type FieldKind = 'string' | 'number' | 'boolean' | 'other';
export type ListField = { name: string; kind: FieldKind; value: string | number | boolean | null; start: number; end: number };
export type ListRecord = { id: string; start: number; end: number; fields: ListField[] };
export type ListData = {
  filePath: string;
  source: string;
  arrayName: string;
  /** Offsets of the array's `[` and `]`. */
  arrayStart: number;
  arrayEnd: number;
  records: ListRecord[];
  columns: { name: string; kind: FieldKind }[];
};

export function findListData(opts: {
  recordId: string;
  filePaths: readonly string[];
  readFile: (path: string) => string | null;
}): ListData | null {
  for (const filePath of opts.filePaths) {
    const path = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!DATA_FILE_PATTERN.test(path)) continue;
    const source = opts.readFile(filePath);
    if (!source) continue;
    const list = readLists(filePath, source).find((l) => l.records.some((r) => r.id === opts.recordId));
    if (list) return list;
  }
  return null;
}

/** Every exported array of records in `source`. */
export function readLists(filePath: string, source: string): ListData[] {
  let ast;
  try {
    ast = parse(source, { sourceType: 'module', plugins: ['jsx', 'typescript'] });
  } catch {
    return [];
  }
  const lists: ListData[] = [];
  traverse(ast, {
    VariableDeclarator(path) {
      if (!t.isIdentifier(path.node.id)) return;
      let init = path.node.init;
      while (init && (t.isTSAsExpression(init) || t.isTSSatisfiesExpression(init))) init = init.expression;
      if (!t.isArrayExpression(init)) return;
      const records: ListRecord[] = [];
      for (const el of init.elements) {
        if (!t.isObjectExpression(el)) continue;
        const fields: ListField[] = [];
        let id: string | null = null;
        for (const p of el.properties) {
          if (!t.isObjectProperty(p) || p.computed) continue;
          const name = t.isIdentifier(p.key) ? p.key.name : t.isStringLiteral(p.key) ? p.key.value : null;
          if (!name) continue;
          const v = p.value;
          const [kind, value]: [FieldKind, ListField['value']] = t.isStringLiteral(v)
            ? ['string', v.value]
            : t.isNumericLiteral(v)
              ? ['number', v.value]
              : t.isBooleanLiteral(v)
                ? ['boolean', v.value]
                : t.isUnaryExpression(v, { operator: '-' }) && t.isNumericLiteral(v.argument)
                  ? ['number', -v.argument.value]
                  : ['other', null];
          fields.push({ name, kind, value, start: v.start!, end: v.end! });
          if (name === 'id' && (kind === 'string' || kind === 'number')) id = String(value);
        }
        if (id != null) records.push({ id, start: el.start!, end: el.end!, fields });
      }
      if (records.length === 0) return;
      const columns: ListData['columns'] = [];
      for (const r of records) {
        for (const f of r.fields) {
          const col = columns.find((c) => c.name === f.name);
          if (!col) columns.push({ name: f.name, kind: f.kind });
          else if (col.kind !== f.kind && f.kind !== 'other') col.kind = col.kind === 'other' ? f.kind : 'other';
        }
      }
      lists.push({
        filePath,
        source,
        arrayName: path.node.id.name,
        arrayStart: init.start!,
        arrayEnd: init.end! - 1,
        records,
        columns,
      });
    },
  });
  return lists;
}

function literalFor(kind: FieldKind, raw: string, like: string): string | null {
  if (kind === 'string') {
    const q = like[0] === "'" || like[0] === '`' ? like[0] : '"';
    return quoteString(raw, q);
  }
  if (kind === 'number') {
    const n = Number(raw.trim());
    return raw.trim() !== '' && Number.isFinite(n) ? String(n) : null;
  }
  if (kind === 'boolean') return raw === 'true' ? 'true' : raw === 'false' ? 'false' : null;
  return null;
}

/** The file with one cell changed, or null when the value does not fit the field's type. */
export function setCell(list: ListData, recordId: string, field: string, raw: string): string | null {
  const rec = list.records.find((r) => r.id === recordId);
  const f = rec?.fields.find((x) => x.name === field);
  if (!rec || !f || f.kind === 'other' || field === 'id') return null;
  const text = literalFor(f.kind, raw, list.source.slice(f.start, f.end));
  if (text == null) return null;
  return list.source.slice(0, f.start) + text + list.source.slice(f.end);
}

/** `alert-7` after `alert-1`..`alert-6`; a plain number id gets the next number. */
export function nextId(list: ListData): string {
  const ids = list.records.map((r) => r.id);
  const m = /^(.*?)(\d+)$/.exec(ids[ids.length - 1] ?? 'item-0');
  const prefix = m?.[1] ?? 'item-';
  let n = 0;
  for (const id of ids) {
    const mm = /^(.*?)(\d+)$/.exec(id);
    if (mm && mm[1] === prefix) n = Math.max(n, Number(mm[2]));
  }
  let candidate = `${prefix}${n + 1}`;
  while (ids.includes(candidate)) candidate = `${prefix}${++n + 1}`;
  return candidate;
}

function recordText(list: ListData, rec: ListRecord, overrides: Record<string, string>): string {
  let text = list.source.slice(rec.start, rec.end);
  const edits = rec.fields
    .filter((f) => overrides[f.name] != null)
    .sort((a, b) => b.start - a.start);
  for (const f of edits) text = text.slice(0, f.start - rec.start) + overrides[f.name] + text.slice(f.end - rec.start);
  return text;
}

function idLiteral(list: ListData, rec: ListRecord, id: string): string {
  const f = rec.fields.find((x) => x.name === 'id')!;
  return f.kind === 'number' && /^\d+$/.test(id) ? id : quoteString(id, list.source[f.start] === "'" ? "'" : '"');
}

/** The separator between records, taken from the file (`,\n  ` or `, `). */
function separator(list: ListData): string {
  const [a, b] = list.records;
  if (a && b) return list.source.slice(a.end, b.start);
  return ', ';
}

function insertAfter(list: ListData, after: ListRecord, text: string): string {
  return list.source.slice(0, after.end) + separator(list) + text + list.source.slice(after.end);
}

/** A copy of the record right after it, with a new id. */
export function duplicateRecord(list: ListData, recordId: string): { content: string; id: string } | null {
  const rec = list.records.find((r) => r.id === recordId);
  if (!rec) return null;
  const id = nextId(list);
  return { content: insertAfter(list, rec, recordText(list, rec, { id: idLiteral(list, rec, id) })), id };
}

/** A new record at the end, shaped like the last one: empty text, 0, false. */
export function addRecord(list: ListData): { content: string; id: string } | null {
  const last = list.records[list.records.length - 1];
  if (!last) return null;
  const id = nextId(list);
  const overrides: Record<string, string> = { id: idLiteral(list, last, id) };
  for (const f of last.fields) {
    if (f.name === 'id') continue;
    if (f.kind === 'string') overrides[f.name] = literalFor('string', '', list.source.slice(f.start, f.end))!;
    if (f.kind === 'number') overrides[f.name] = '0';
    if (f.kind === 'boolean') overrides[f.name] = 'false';
  }
  return { content: insertAfter(list, last, recordText(list, last, overrides)), id };
}

export function deleteRecord(list: ListData, recordId: string): string | null {
  const i = list.records.findIndex((r) => r.id === recordId);
  if (i < 0) return null;
  const rec = list.records[i]!;
  const next = list.records[i + 1];
  const prev = list.records[i - 1];
  // Remove the record with the separator after it (or before it, for the last one).
  let start = rec.start;
  let end = rec.end;
  if (next) end = next.start;
  else if (prev) start = prev.end;
  else {
    const after = list.source.slice(rec.end, list.arrayEnd);
    end = rec.end + (/^\s*,/.exec(after)?.[0].length ?? 0);
  }
  return list.source.slice(0, start) + list.source.slice(end);
}

/** The records in a new order: `recordId` moved to position `toIndex`. Separators stay in place. */
export function moveRecord(list: ListData, recordId: string, toIndex: number): string | null {
  const from = list.records.findIndex((r) => r.id === recordId);
  if (from < 0 || toIndex < 0 || toIndex >= list.records.length || from === toIndex) return null;
  const texts = list.records.map((r) => list.source.slice(r.start, r.end));
  const [moved] = texts.splice(from, 1);
  texts.splice(toIndex, 0, moved!);
  let out = list.source;
  for (let i = list.records.length - 1; i >= 0; i--) {
    const r = list.records[i]!;
    out = out.slice(0, r.start) + texts[i] + out.slice(r.end);
  }
  return out;
}
